import React from "react";
import PropTypes from "prop-types";
import Drawer from "@mui/material/Drawer";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import SFIconButton from "../button/iconButton";
import SFSkeleton from "../skeleton";
import { SFCloseIcon } from "../icons";
import getStyled from "../../hooks/getStyled";
import SFDivider from "../divider";
import { useTranslation } from "react-i18next";

const { styled } = getStyled();

const StyledPaddedHeader = styled(SFStack, { shouldForwardProp: false })(
  ({ theme, disableHeaderBottomBorder }) => ({
    paddingLeft: "20px",
    paddingRight: "8px",
    paddingTop: "12px",
    paddingBottom: "12px",
    borderBottom: disableHeaderBottomBorder
      ? 0
      : `solid 1px ${theme.palette.grey225.main}`,
  })
);

const StyledHeader = styled(SFStack)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.white.main,
  zIndex: 2,
  width: "100%",
}));

const StyledBody = styled("div", { shouldForwardProp: false })(
  ({ theme, disableGutters, disablePaddingTop }) => ({
    display: "flex",
    flexDirection: "column",
    // These two properties allow the body to fill the available space
    // without affecting the footer
    height: 0,
    flex: "1 1 auto",
    overflowY: "auto",
    paddingBottom: theme.spacing(2.5),
    ...(!disableGutters && {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
    }),
    ...(!disablePaddingTop && {
      paddingTop: theme.spacing(2.5),
    }),
  })
);

const StyledFooterWrapper = styled("div")(({ theme }) => ({
  position: "sticky",
  backgroundColor: "inherit",
  bottom: 0,
  "& .footer": {
    padding: `${theme.spacing(1.5)} ${theme.spacing(2.5)}`,
  },
  borderTop: `solid 1px ${theme.palette.grey225.main}`,
  boxShadow: "0px -2px 4px 0px rgba(183, 183, 183, 0.20)",
}));

const StyledWrapper = styled(SFStack, {
  shouldForwardProp: false,
})(({ theme, maxWidth }) => ({
  maxWidth: maxWidth,
  // This leaves a 32px space so that the drawer does not cover the entire screen
  width: "calc(100vw - 32px)",
  // When viewport width is 380px or smaller (mobile view specific to drawer)
  // width is set to 100% of the viewport so that it covers the entire screen
  [theme.breakpoints.down("380")]: {
    width: "100vw",
    maxWidth: "auto",
  },
  flex: 1,
}));

const StyledIconButton = styled(SFIconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey100.main,
  width: "24px",
  height: "24px",
  borderRadius: "50%",
}));

const sizes = {
  s: 380,
  m: 768,
  l: 980,
  xl: 1382,
  default: 768,
};
const SFDrawer = ({
  open,
  side = "right",
  header,
  footer,
  onClose,
  children,
  size = "m",
  disableGutters = false,
  disablePaddingTop = false,
  disableHeaderBottomBorder = false,
  headerOptions,
  ignoreHtml2Canvas = false,
  className,
}) => {
  let maxWidth = 768;
  const { t } = useTranslation();

  maxWidth = sizes[size] || sizes.default;

  const toggleDrawer = (open) => (event) => {
    //todo: do we want to close drawer on click outside?
    // if (
    //   event.type === "keydown" &&
    //   (event.key === "Tab" || event.key === "Shift")
    // ) {
    //   return;
    // }
    onClose();
  };
  return (
    <Drawer
      {...(ignoreHtml2Canvas && { "data-html2canvas-ignore": true })}
      anchor={side}
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        "& .MuiModal-root": {
          overflowY: "hidden",
          "& > .MuiPaper-root": {
            overflow: "-moz-scrollbars-vertical",
            overflowY: "scroll",
          },
        },
      }}
      className={className}
    >
      <StyledWrapper maxWidth={maxWidth}>
        <StyledPaddedHeader
          alignItems="center"
          justifyContent="space-between"
          disableHeaderBottomBorder={disableHeaderBottomBorder}
        >
          <StyledHeader>
            <SFStack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {header ? (
                <SFTypography variant="cardSectionHeading">
                  {header}
                </SFTypography>
              ) : (
                <SFSkeleton
                  showHeader={true}
                  showBody={false}
                  showFooter={false}
                  noBorder
                />
              )}
              <SFStack direction="row" alignItems="center">
                <SFStack direction="row" alignItems="center" spacing={2}>
                  {headerOptions}
                  <StyledIconButton onClick={toggleDrawer(false)} ariaLabel={t("drawer.closeButtonAriaLabel")}>
                    <SFCloseIcon color="grey500" size="small" />
                  </StyledIconButton>
                </SFStack>
              </SFStack>
            </SFStack>
          </StyledHeader>
        </StyledPaddedHeader>
        <StyledBody
          disableGutters={disableGutters}
          disablePaddingTop={disablePaddingTop}
        >
          {children}
        </StyledBody>
      </StyledWrapper>
      {footer && (
        <StyledFooterWrapper>
          <SFDivider />
          <div className="footer">{footer}</div>
        </StyledFooterWrapper>
      )}
    </Drawer>
  );
};

SFDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  side: PropTypes.oneOf(["top", "left", "bottom", "right"]),
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  footer: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  size: PropTypes.oneOf(["s", "m", "l", "xl"]),
  disableGutters: PropTypes.bool,
  disablePaddingTop: PropTypes.bool,
  disableHeaderBottomBorder: PropTypes.bool,
  headerOptions: PropTypes.object,
  ignoreHtml2Canvas: PropTypes.bool,
};

export default SFDrawer;
